/*
  Calculates 4 upcoming astrological events (lunar phases + solstices/
  equinoxes) for the current month, purely client-side - no API calls.

  Lunar phase math: standard synodic-month approximation against a known
  reference new moon (Jan 6, 2000, 18:14 UTC). For each of the 4 target
  phase fractions, every day in the month is checked and the closest match
  is used - accurate to within about a day, which is fine for a decorative
  feature.

  Solstice/equinox dates are fixed approximations (Mar 20 / Jun 21 / Sep 22 /
  Dec 21). Real solstice/equinox timing shifts by roughly +/-1 day year to
  year - this is a known limitation, not an attempt at precise ephemeris data.

  New Moon zodiac placement is intentionally omitted: determining it
  accurately requires real ecliptic longitude calculation, which is out of
  scope for simple synodic math with no external API.

  Zodiac sign / sun-sign-change dates use the standard well-known tropical
  zodiac calendar boundaries (e.g. Sun enters Aries ~Mar 21) - approximate
  and static year to year, same category of limitation as the solstice/
  equinox dates above.

  Harvest Moon: the full moon closest to the September equinox (fixed
  approximation, Sep 22), which can land in either September or October -
  both months' full moons are computed and compared. Same accuracy caveat
  as the lunar phase math above.

  Blood Moon: matched against a hardcoded, manually-maintained list of REAL
  confirmed total lunar eclipse dates (see getBloodMoonDates) - never
  estimated or extrapolated. That list needs manual updates as further
  eclipses are confirmed.
*/
class SkyThisMonth extends HTMLElement {
  connectedCallback() {
    this.render();
    this.bindZodiacFlip();
  }

  render() {
    const monthLabel = this.querySelector('[data-sky-month]');
    if (monthLabel) {
      monthLabel.textContent = SkyThisMonth.formatMonthYear(new Date());
    }

    const grid = this.querySelector('[data-sky-grid]');
    if (!grid) return;

    const zodiacMarkup = SkyThisMonth.zodiacCardMarkup(new Date());
    const events = SkyThisMonth.getEventsForCurrentMonth();
    const eventsMarkup = events.map((event, index) => SkyThisMonth.cardMarkup(event, index)).join('');

    grid.innerHTML = zodiacMarkup + eventsMarkup;
  }

  // Desktop hover-flip is handled purely in CSS via @media (hover: hover).
  // This only handles tap-to-flip for touch/non-hover devices: first tap
  // flips, second tap (or a tap outside the card) flips back.
  bindZodiacFlip() {
    const zodiacCard = this.querySelector('[data-zodiac-card]');
    if (!zodiacCard) return;

    const flipTrigger = zodiacCard.querySelector('.sky-card__flip-inner') || zodiacCard;

    const toggle = () => zodiacCard.classList.toggle('is-flipped');

    flipTrigger.addEventListener('click', (event) => {
      event.stopPropagation();
      toggle();
    });

    flipTrigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    });

    document.addEventListener('click', (event) => {
      if (zodiacCard.classList.contains('is-flipped') && !zodiacCard.contains(event.target)) {
        zodiacCard.classList.remove('is-flipped');
      }
    });
  }

  static cardMarkup(event, index) {
    // Waxing Half Moon and Full Moon are pinned to their own dedicated
    // colors (not the --1/--4 cycling colors) so each phase's color stays
    // consistent month to month instead of drifting with array position.
    // Blood Moon (a rarer override of Full Moon) still takes priority.
    const variant = event.isBloodMoon
      ? 'blood-moon'
      : event.type === 'first_quarter'
        ? 'waxing'
        : event.type === 'full_moon'
          ? 'lavender'
          : (index % 4) + 1;
    const phaseShapeClass = SkyThisMonth.getPhaseShapeClass(event.type);
    const iconClasses = ['sky-card__icon', `sky-card__icon--${variant}`, phaseShapeClass].filter(Boolean).join(' ');
    const baseDelays = [0.05, 0.1, 0.15];
    const sparkles = baseDelays
      .map((baseDelay) => `<span class="sky-card__sparkle" style="${SkyThisMonth.randomSparkleStyle(baseDelay)}"></span>`)
      .join('');

    return `
      <li class="sky-card sky-card--${variant}">
        <span class="${iconClasses}" aria-hidden="true">
          ${sparkles}
        </span>
        <p class="sky-card__date">${event.dateLabel}</p>
        <h3 class="sky-card__heading">${event.heading}</h3>
        <p class="sky-card__description">${event.description}</p>
      </li>
    `;
  }

  // New Moon/Waxing/Waning get a distinct outline or half-filled shape class
  // (defined in section-sky-this-month.css) so each phase is recognizable
  // without relying on color alone. Full Moon and non-lunar event types
  // (solstice/equinox/sun-sign-change) return null and keep the default
  // filled-sphere icon look.
  static getPhaseShapeClass(type) {
    switch (type) {
      case 'new_moon':
        return 'sky-card__icon--new-moon';
      case 'first_quarter':
        return 'sky-card__icon--first-quarter';
      case 'last_quarter':
        return 'sky-card__icon--last-quarter';
      default:
        return null;
    }
  }

  static zodiacCardMarkup(date) {
    const sign = SkyThisMonth.getZodiacSign(date.getMonth(), date.getDate());
    const glyphs = {
      Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
      Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓'
    };
    const horoscopes = {
      Aries: 'Your fire wants a direction, not just an outlet. Channel restlessness into one clear goal instead of scattering it everywhere.',
      Taurus: "Slow down before you decide. What feels urgent right now may look different once you've actually rested.",
      Gemini: "You're collecting more information than you're processing. Pick one thread and follow it all the way through.",
      Cancer: "Protecting your peace isn't the same as hiding from what needs attention. Let the right people in.",
      Leo: "Being seen and being understood aren't always the same thing. Seek the second one this month.",
      Virgo: 'Not everything broken needs fixing today. Some things just need noticing.',
      Libra: "The scales don't need perfect balance - they need honesty about what's actually on each side.",
      Scorpio: "What you've been quietly sensing is worth trusting, even without proof yet.",
      Sagittarius: 'The horizon will still be there next week. This month rewards depth over distance.',
      Capricorn: 'Discipline without rest becomes depletion. Build in the pause, not just the push.',
      Aquarius: "Your ideas are ahead of most rooms you're in. Find the few people worth explaining them to.",
      Pisces: "Boundaries aren't walls - they're how your compassion survives contact with reality."
    };

    return `
      <li class="sky-card sky-card--zodiac" data-zodiac-card>
        <div class="sky-card__flip-inner" tabindex="0" role="button" aria-label="Flip to reveal this month's horoscope for ${sign}">
          <div class="sky-card__flip-front">
            <span class="sky-card__zodiac-glyph" aria-hidden="true">${glyphs[sign]}</span>
            <p class="sky-card__zodiac-label caption-with-letter-spacing">Zodiac of the Month</p>
            <h3 class="sky-card__heading">${sign}</h3>
          </div>
          <div class="sky-card__flip-back">
            <p class="sky-card__horoscope">${horoscopes[sign]}</p>
          </div>
        </div>
      </li>
    `;
  }

  // Standard tropical zodiac boundaries. Each check is mutually exclusive
  // with every other (real calendar sign ranges don't overlap), so order
  // doesn't affect correctness - kept in calendar order for readability.
  static getZodiacSign(month, day) {
    if ((month === 0 && day >= 20) || (month === 1 && day <= 18)) return 'Aquarius';
    if ((month === 1 && day >= 19) || (month === 2 && day <= 20)) return 'Pisces';
    if ((month === 2 && day >= 21) || (month === 3 && day <= 19)) return 'Aries';
    if ((month === 3 && day >= 20) || (month === 4 && day <= 20)) return 'Taurus';
    if ((month === 4 && day >= 21) || (month === 5 && day <= 20)) return 'Gemini';
    if ((month === 5 && day >= 21) || (month === 6 && day <= 22)) return 'Cancer';
    if ((month === 6 && day >= 23) || (month === 7 && day <= 22)) return 'Leo';
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Virgo';
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Libra';
    if ((month === 9 && day >= 23) || (month === 10 && day <= 21)) return 'Scorpio';
    if ((month === 10 && day >= 22) || (month === 11 && day <= 21)) return 'Sagittarius';
    return 'Capricorn'; // month 11, day >= 22, or month 0, day < 20
  }

  // The sun-sign-change transition date for a given month, if any - every
  // month has exactly one, since the 12 sign boundaries land roughly once
  // per calendar month.
  static getSunSignChangeForMonth(year, month) {
    const transitions = [
      { month: 0, day: 20, sign: 'Aquarius' },
      { month: 1, day: 19, sign: 'Pisces' },
      { month: 2, day: 21, sign: 'Aries' },
      { month: 3, day: 20, sign: 'Taurus' },
      { month: 4, day: 21, sign: 'Gemini' },
      { month: 5, day: 21, sign: 'Cancer' },
      { month: 6, day: 23, sign: 'Leo' },
      { month: 7, day: 23, sign: 'Virgo' },
      { month: 8, day: 23, sign: 'Libra' },
      { month: 9, day: 23, sign: 'Scorpio' },
      { month: 10, day: 22, sign: 'Sagittarius' },
      { month: 11, day: 22, sign: 'Capricorn' }
    ];

    const transition = transitions.find((t) => t.month === month);
    if (!transition) return null;

    const eventDate = new Date(Date.UTC(year, month, transition.day, 12, 0, 0));
    return SkyThisMonth.buildEvent('sun_sign_change', eventDate, transition.sign);
  }

  // Computes a random position/size/timing for one sparkle, applied once at
  // render time (called once per page load) and baked in as inline styles -
  // hover only toggles opacity/scale/animation via CSS, so the randomized
  // values stay fixed for the lifetime of the page view.
  static randomSparkleStyle(baseDelaySeconds) {
    const iconRadius = 18; // .sky-card__icon is 3.6rem (36px) diameter
    const angle = Math.random() * Math.PI * 2;
    const distanceFromEdge = 8 + Math.random() * 8; // 8-16px beyond the icon's edge
    const totalDistance = iconRadius + distanceFromEdge;
    const x = Math.cos(angle) * totalDistance;
    const y = Math.sin(angle) * totalDistance;
    const size = 4 + Math.random() * 3; // 4-7px
    const jitter = Math.random() * 0.1 - 0.05; // +/- 0.05s
    const transitionDelay = Math.max(0, baseDelaySeconds + jitter);
    const animationDelay = transitionDelay + 0.25; // let the fade-in transition finish first

    return [
      `top: calc(50% + ${y.toFixed(1)}px)`,
      `left: calc(50% + ${x.toFixed(1)}px)`,
      `width: ${size.toFixed(1)}px`,
      `height: ${size.toFixed(1)}px`,
      `margin-top: -${(size / 2).toFixed(1)}px`,
      `margin-left: -${(size / 2).toFixed(1)}px`,
      `transition-delay: ${transitionDelay.toFixed(2)}s`,
      `animation-delay: ${animationDelay.toFixed(2)}s`
    ].join('; ');
  }

  static getEventsForCurrentMonth() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    let events = SkyThisMonth.getEventsForMonth(year, month);

    // Defensive only: every month now always has >=5 candidates (4 lunar
    // phases + sun-sign-change, present every month), so this should be
    // unreachable in practice - kept in case that ever changes.
    if (events.length < 4) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      const nextEvents = SkyThisMonth.getEventsForMonth(nextYear, nextMonth);
      events = events.concat(nextEvents);
    }

    return SkyThisMonth.selectPriorityEvents(events, now);
  }

  // Explicit priority order when there are more than 4 candidates (always
  // true in solstice/equinox months: 4 lunar + seasonal + sun-sign-change
  // = 6):
  //   1. New Moon / Full Moon - never dropped.
  //   2. Solstice/Equinox - rarer (4x/year) than sun-sign-change (12x/year),
  //      so it outranks sun-sign-change.
  //   3. First/Last Quarter - both kept unless capacity still isn't enough
  //      after dropping sun-sign-change, in which case whichever quarter
  //      phase is chronologically furthest from "today" is dropped, since
  //      New Moon/Full Moon are the more commonly-tracked phases.
  //   4. Sun-sign-change - lowest priority, dropped first.
  static selectPriorityEvents(candidates, referenceDate) {
    const seasonalTypes = ['spring_equinox', 'summer_solstice', 'autumn_equinox', 'winter_solstice'];

    const core = candidates.filter((e) => e.type === 'new_moon' || e.type === 'full_moon');
    const seasonal = candidates.filter((e) => seasonalTypes.includes(e.type));
    let quarters = candidates.filter((e) => e.type === 'first_quarter' || e.type === 'last_quarter');
    let sunSign = candidates.filter((e) => e.type === 'sun_sign_change');

    let selected = core.concat(seasonal, quarters, sunSign);

    while (selected.length > 4) {
      if (sunSign.length > 0) {
        sunSign = [];
      } else if (quarters.length > 1) {
        quarters = quarters
          .slice()
          .sort((a, b) => Math.abs(a.date - referenceDate) - Math.abs(b.date - referenceDate))
          .slice(0, quarters.length - 1);
      } else {
        // Shouldn't happen with only 6 possible candidates max, but avoids
        // an infinite loop if the candidate mix ever changes unexpectedly.
        break;
      }

      selected = core.concat(seasonal, quarters, sunSign);
    }

    return selected.sort((a, b) => a.date - b.date);
  }

  static getEventsForMonth(year, month) {
    const events = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const targets = [
      { fraction: 0, type: 'new_moon' },
      { fraction: 0.25, type: 'first_quarter' },
      { fraction: 0.5, type: 'full_moon' },
      { fraction: 0.75, type: 'last_quarter' }
    ];

    targets.forEach((target) => {
      let closestDay = 1;
      let closestDistance = Infinity;

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
        const phase = SkyThisMonth.getMoonPhase(date);
        let distance = Math.abs(phase - target.fraction);
        distance = Math.min(distance, 1 - distance);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestDay = day;
        }
      }

      const eventDate = new Date(Date.UTC(year, month, closestDay, 12, 0, 0));
      events.push(SkyThisMonth.buildEvent(target.type, eventDate));
    });

    const seasonalMarkers = {
      2: { day: 20, type: 'spring_equinox' },
      5: { day: 21, type: 'summer_solstice' },
      8: { day: 22, type: 'autumn_equinox' },
      11: { day: 21, type: 'winter_solstice' }
    };

    if (seasonalMarkers[month]) {
      const marker = seasonalMarkers[month];
      const eventDate = new Date(Date.UTC(year, month, marker.day, 12, 0, 0));
      events.push(SkyThisMonth.buildEvent(marker.type, eventDate));
    }

    const sunSignEvent = SkyThisMonth.getSunSignChangeForMonth(year, month);
    if (sunSignEvent) events.push(sunSignEvent);

    return events;
  }

  static getMoonPhase(date) {
    const synodicMonth = 29.53058867;
    const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14, 0);
    const daysSince = (date.getTime() - knownNewMoon) / 86400000;
    let phase = (daysSince / synodicMonth) % 1;
    if (phase < 0) phase += 1;
    return phase;
  }

  // Standalone full-moon-date finder for a single month. Used by the
  // Harvest Moon comparison, which needs to check the adjacent month's
  // full moon too, not just the current month's (same closest-day search
  // as the per-month event loop above, just isolated to one phase target).
  static getFullMoonDateForMonth(year, month) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let closestDay = 1;
    let closestDistance = Infinity;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
      const phase = SkyThisMonth.getMoonPhase(date);
      let distance = Math.abs(phase - 0.5);
      distance = Math.min(distance, 1 - distance);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestDay = day;
      }
    }

    return new Date(Date.UTC(year, month, closestDay, 12, 0, 0));
  }

  // The Harvest Moon is the full moon that falls closest to the September
  // equinox - it can land in either September or October depending on the
  // year, so both candidates are computed and compared.
  static getHarvestMoonDate(year) {
    const equinox = new Date(Date.UTC(year, 8, 22, 12, 0, 0));
    const septemberFullMoon = SkyThisMonth.getFullMoonDateForMonth(year, 8);
    const octoberFullMoon = SkyThisMonth.getFullMoonDateForMonth(year, 9);

    const septemberDistance = Math.abs(septemberFullMoon - equinox);
    const octoberDistance = Math.abs(octoberFullMoon - equinox);

    return septemberDistance <= octoberDistance ? septemberFullMoon : octoberFullMoon;
  }

  static isHarvestMoon(date) {
    return SkyThisMonth.isSameUTCDate(date, SkyThisMonth.getHarvestMoonDate(date.getUTCFullYear()));
  }

  static isSameUTCDate(a, b) {
    return (
      a.getUTCFullYear() === b.getUTCFullYear() &&
      a.getUTCMonth() === b.getUTCMonth() &&
      a.getUTCDate() === b.getUTCDate()
    );
  }

  // Hardcoded, manually-maintained list of REAL confirmed total lunar
  // eclipse dates (source: published astronomical eclipse calendars) - not
  // estimated or extrapolated. Add new dates here as further eclipses are
  // confirmed; this list will go stale over time otherwise.
  static getBloodMoonDates() {
    return [
      { year: 2028, month: 11, day: 31 }, // Dec 31, 2028
      { year: 2029, month: 5, day: 26 } // Jun 26, 2029
    ];
  }

  // +/-1 day tolerance since the synodic-month full-moon search above is
  // only accurate to within about a day (see file header).
  static isBloodMoon(date) {
    return SkyThisMonth.getBloodMoonDates().some((eclipse) => {
      const eclipseDate = new Date(Date.UTC(eclipse.year, eclipse.month, eclipse.day, 12, 0, 0));
      const dayDistance = Math.abs(date - eclipseDate) / 86400000;
      return dayDistance <= 1;
    });
  }

  static buildEvent(type, date, extra) {
    const copy = SkyThisMonth.getEventCopy(type, date, extra);
    return {
      type,
      date,
      dateLabel: SkyThisMonth.formatDateLabel(date),
      heading: copy.heading,
      description: copy.description,
      isBloodMoon: copy.isBloodMoon || false
    };
  }

  static formatDateLabel(date) {
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${monthNames[date.getUTCMonth()]} ${day}`;
  }

  static formatMonthYear(date) {
    const monthNames = [
      'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
      'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
    ];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  }

  static getEventCopy(type, date, extra) {
    const month = date.getUTCMonth();

    // Common Farmer's-Almanac-style seasonal full moon names (Northern Hemisphere).
    const fullMoonNames = [
      'Wolf', 'Snow', 'Worm', 'Pink', 'Flower', 'Strawberry',
      'Buck', 'Sturgeon', 'Corn', 'Hunter', 'Beaver', 'Cold'
    ];

    switch (type) {
      case 'new_moon':
        return { heading: 'New Moon', description: "Set intentions for what's ahead." };
      case 'first_quarter':
        return { heading: 'Waxing Half Moon', description: 'Take action on your intentions.' };
      case 'full_moon': {
        // Priority: Blood Moon > Harvest Moon > seasonal name > generic fallback.
        if (SkyThisMonth.isBloodMoon(date)) {
          return {
            heading: 'Blood Moon',
            description: 'A total lunar eclipse - rare and potent. Transformation moves fast now.',
            isBloodMoon: true
          };
        }
        if ((month === 8 || month === 9) && SkyThisMonth.isHarvestMoon(date)) {
          return { heading: 'Harvest Moon', description: 'The moon that lights the harvest - gather what you have grown.' };
        }
        return { heading: `Full ${fullMoonNames[month]} Moon`, description: 'Release what no longer serves you.' };
      }
      case 'last_quarter':
        return { heading: 'Waning Half Moon', description: 'Cut cords; tidy the altar.' };
      case 'spring_equinox':
        return { heading: 'Spring Equinox', description: 'Balance light and dark, begin anew.' };
      case 'summer_solstice':
        return { heading: 'Summer Solstice', description: 'Step into your fullest power.' };
      case 'autumn_equinox':
        return { heading: 'Autumn Equinox', description: 'Balance, then lean into the dark.' };
      case 'winter_solstice':
        return { heading: 'Winter Solstice', description: 'Rest in the stillness before rebirth.' };
      case 'sun_sign_change':
        return { heading: `Sun Enters ${extra}`, description: 'A new season begins.' };
      default:
        return { heading: '', description: '' };
    }
  }
}

customElements.define('sky-this-month', SkyThisMonth);
